import React, { useState, useRef, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAnalytics } from '../../context/AnalyticsContext';
import { 
  Send, 
  MessageCircle, 
  Database, 
  TrendingUp,
  BarChart3,
  PieChart,
  Filter,
  Lightbulb,
  Copy,
  Download,
  RefreshCw,
  User,
  Bot,
  Clock
} from 'lucide-react';

interface AnalyticsChatInterfaceProps {
  provider: 'zoho' | 'powerbi';
}

const AnalyticsChatInterface: React.FC<AnalyticsChatInterfaceProps> = ({ provider }) => {
  const { state, actions } = useAnalytics();
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const providerState = state.providers[provider];
  const schema = state.schema[provider];
  const activeConversation = state.conversations.find(c => c.id === state.activeConversationId);
  const isReady = providerState.authorized && schema?.data && state.isAnalyticsChatEnabled;
 
  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !isReady) return;

    const messageText = message.trim();
    setMessage('');
    setIsTyping(true);

    try {
      let conversationId = state.activeConversationId;
      
      // Start new conversation if none exists
      if (!conversationId) {
        conversationId = actions.startAnalyticsConversation(provider);
      }

      // Analyze message for analytics context
      const analyticsContext = analyzeMessageForContext(messageText, schema?.data?.tables || []);

      await actions.sendAnalyticsMessage(conversationId, messageText, analyticsContext);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const analyzeMessageForContext = (message: string, tables: any[]) => {
    const lowerMessage = message.toLowerCase();
    const relevantTables: string[] = [];
    const relevantColumns: string[] = [];
    let queryType: 'aggregation' | 'filter' | 'visualization' | 'insight' = 'insight';
    let suggestedVisualization: 'chart' | 'table' | 'metric' | undefined;

    // Find relevant tables
    tables.forEach(table => {
      if (lowerMessage.includes(table.name.toLowerCase()) || 
          lowerMessage.includes(table.displayName?.toLowerCase() || '')) {
        relevantTables.push(table.name);
        
        // Find relevant columns
        table.columns.forEach((column: any) => {
          if (lowerMessage.includes(column.name.toLowerCase())) {
            relevantColumns.push(`${table.name}.${column.name}`);
          }
        });
      }
    });

    // Determine query type and visualization
    if (lowerMessage.includes('show') || lowerMessage.includes('list') || lowerMessage.includes('display')) {
      queryType = 'filter';
      suggestedVisualization = 'table';
    } else if (lowerMessage.includes('count') || lowerMessage.includes('sum') || 
               lowerMessage.includes('average') || lowerMessage.includes('total')) {
      queryType = 'aggregation';
      suggestedVisualization = 'metric';
    } else if (lowerMessage.includes('chart') || lowerMessage.includes('graph') || 
               lowerMessage.includes('trend') || lowerMessage.includes('over time')) {
      queryType = 'visualization';
      suggestedVisualization = 'chart';
    }

    return {
      tables: relevantTables,
      columns: relevantColumns,
      queryType,
      suggestedVisualization,
    };
  };

  const renderMessage = (msg: any, index: number) => {
    const isUser = msg.sender === 'user';
    
    return (
      <div key={msg.id || index} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`flex max-w-3xl ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            isUser ? 'bg-blue-100 ml-2' : 'bg-gray-100 mr-2'
          }`}>
            {isUser ? <User className="w-4 h-4 text-blue-600" /> : <Bot className="w-4 h-4 text-gray-600" />}
          </div>
          
          <div className={`px-4 py-2 rounded-lg ${
            isUser 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-900'
          }`}>
            <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
            
            {msg.analyticsContext && (
              <div className="mt-2 text-xs opacity-75">
                {msg.analyticsContext.tables.length > 0 && (
                  <div>Tables: {msg.analyticsContext.tables.join(', ')}</div>
                )}
                {msg.analyticsContext.queryType && (
                  <div>Query Type: {msg.analyticsContext.queryType}</div>
                )}
              </div>
            )}
            
            {msg.chart && (
              <div className="mt-3 bg-white rounded-lg p-3">
                <img 
                  src={`data:image/${msg.chart.format};base64,${msg.chart.base64}`}
                  alt="Analytics Chart"
                  className="w-full h-auto rounded"
                  onError={(e) => {
                    console.error(`  ❌ Failed to load chart image for message ${index}:`, e);
                    console.error('  Chart data:', msg.chart);
                  }}
                />
                {msg.rowCount && (
                  <div className="text-xs text-gray-600 mt-2">
                    {msg.rowCount} rows analyzed
                  </div>
                )}
              </div>
            )}
            
            {msg.rows && msg.rows.length > 0 && (
              <div className="mt-3 bg-white rounded-lg p-3 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {msg.columns?.slice(0, 5).map((col: any, idx: number) => {
                        const rawLabel = typeof col === 'string' ? col : (col.label ?? col.key ?? String(col));
                        const colLabel = typeof rawLabel === 'string' ? rawLabel.replace(/^\uFEFF/, '') : String(rawLabel);
                        return (
                          <th key={idx} className="text-left py-2 px-3 font-semibold text-gray-700">
                            {colLabel}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {msg.rows.slice(0, 10).map((row, rowIdx) => (
                      <tr key={rowIdx} className="border-b border-gray-100">
                        {msg.columns?.slice(0, 5).map((col: any, colIdx: number) => {
                          const rawKey = typeof col === 'string' ? col : (col.key ?? col.label ?? '');
                          const colKey = typeof rawKey === 'string' ? rawKey.replace(/^\uFEFF/, '') : String(rawKey);
                          return (
                            <td key={colIdx} className="py-2 px-3 text-gray-800">
                              {String(row[colKey] ?? row[rawKey] ?? '')}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {msg.rows.length > 10 && (
                  <div className="text-xs text-gray-600 mt-2 text-center">
                    Showing 10 of {msg.rows.length} rows
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs opacity-75">
                {new Date(msg.timestamp).toLocaleTimeString()}
              </span>
              
              {!isUser && (
                <div className="flex space-x-1 ml-2">
                  <button
                    onClick={() => navigator.clipboard.writeText(msg.content)}
                    className="opacity-75 hover:opacity-100"
                    title="Copy message"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const suggestedQuestions = [
    'Show me the top 10 customers by revenue',
    'What is the total sales for this month?',
    'Display a chart of sales trends over the last 6 months',
    'Which products have the highest profit margins?',
    'Show me the conversion rate by marketing channel',
  ];

  const handleSuggestionClick = (suggestion: string) => {
    setMessage(suggestion);
    inputRef.current?.focus();
  };

  if (!isReady) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">Analytics Chat Not Ready</h3>
          
          {!providerState.authorized && (
            <p className="text-text-secondary mb-4">
              Please connect your {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} account first.
            </p>
          )}
          
          {providerState.authorized && !schema?.data && (
            <p className="text-text-secondary mb-4">
              Please discover your data schema to enable analytics conversations.
            </p>
          )}
          
          <div className="space-y-2">
            {!providerState.authorized && (
              <div className="flex items-center justify-center text-red-600">
                <Database className="w-4 h-4 mr-2" />
                <span className="text-sm">Provider not connected</span>
              </div>
            )}
            {!schema?.data && providerState.authorized && (
              <div className="flex items-center justify-center text-yellow-600">
                <Database className="w-4 h-4 mr-2" />
                <span className="text-sm">Schema not discovered</span>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} Chat
              </h3>
              <p className="text-sm text-text-secondary">
                Ask questions about your data in natural language
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              <span className="text-sm font-medium">Connected</span>
            </div>
            
            {schema?.data && (
              <div className="text-sm text-text-secondary">
                {schema.data.tables.length} tables available
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Chat Messages */}
      <Card className="flex-1 flex flex-col">
        <div className="flex-1 p-4 overflow-y-auto max-h-96">
          {!activeConversation || activeConversation.messages.length === 0 ? (
            <div className="text-center py-8">
              <Bot className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-text-primary mb-2">
                Welcome to Analytics Chat!
              </h4>
              <p className="text-text-secondary mb-6">
                Ask me anything about your data. I can help you explore, analyze, and visualize your information.
              </p>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-text-primary mb-3">Try asking:</h5>
                <div className="grid gap-2">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(question)}
                      className="text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 text-sm text-blue-800 transition-colors"
                    >
                      <Lightbulb className="w-4 h-4 inline mr-2" />
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div>
              {activeConversation.messages.map((msg, index) => renderMessage(msg, index))}
              
              {isTyping && (
                <div className="flex justify-start mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-2">
                      <Bot className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="bg-gray-100 px-4 py-2 rounded-lg">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <div className="flex-1">
              <Input
                ref={inputRef}
                type="text"
                placeholder="Ask about your data..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={isTyping}
              />
            </div>
            <Button 
              onClick={handleSendMessage} 
              disabled={!message.trim() || isTyping}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex items-center justify-between mt-2 text-xs text-text-secondary">
            <div className="flex items-center space-x-4">
              <span>Press Enter to send</span>
              {schema?.data && (
                <span>
                  <Database className="w-3 h-3 inline mr-1" />
                  {schema.data.tables.length} tables accessible
                </span>
              )}
            </div>
            
            {activeConversation && (
              <div className="flex items-center space-x-2">
                <Clock className="w-3 h-3" />
                <span>
                  {activeConversation.messages.length} messages
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AnalyticsChatInterface;